import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import PageHeader from '../components/PageHeader';
import Widget from '../components/Widget';
import StatCard from '../components/StatCard';
import StatusBadge from '../components/StatusBadge';
import ProviderAvatar from '../components/ProviderAvatar';
import DonutChart from '../components/charts/DonutChart';
import BarList from '../components/charts/BarList';
import MiniBars from '../components/charts/MiniBars';
import { Loading, ErrorState, EmptyState } from '../components/States';
import Icon from '../components/Icon';
import { currency, number, relativeTime } from '../lib/format';
import { ICONS } from '../lib/icons';
import { getRecent, recordVisit } from '../lib/recent';

// Spend over time (forecast, previous month, daily trend) is only populated
// when the platform has actually measured it; until then the backend returns
// zero/empty rather than extrapolating, and these render as an em dash.
const DASH = '—';
const money = (v, cur) => (v ? currency(v, cur) : DASH);

const CATEGORY_COLORS = {
  Compute: '#6366f1',
  Storage: '#0ea5e9',
  Database: '#22c55e',
  Networking: '#f59e0b',
  Security: '#ef4444',
  Analytics: '#a855f7',
};

const STATUS_META = {
  running: { color: 'var(--ok)', label: 'Running' },
  provisioning: { color: 'var(--info)', label: 'Provisioning' },
  stopped: { color: 'var(--text-faint)', label: 'Stopped' },
  error: { color: 'var(--danger)', label: 'Error' },
};

export default function Overview() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(async () => {
    const providers = await api.getProviders();
    const [overview, costs, resources] = await Promise.all([
      api.getOverview(),
      api.getCosts(),
      api.getResources(),
    ]);
    const serviceLists = await Promise.all(
      providers.map((p) => api.getServices(p.id))
    );
    const byId = Object.fromEntries(providers.map((p) => [p.id, p]));
    const services = serviceLists.flat().map((s) => ({
      ...s,
      providerShort: byId[s.providerId]?.shortName || s.providerId,
    }));
    return { overview, costs, resources, providers, services };
  }, []);

  if (loading) return <Loading label="Loading dashboard…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const { overview: o, costs, resources, providers, services } = data;
  const providersById = Object.fromEntries(providers.map((p) => [p.id, p]));

  // Recently visited services — genuinely visited ones only. The list stays
  // empty until you open a service; it is never padded from the catalog.
  const catalogByKey = Object.fromEntries(
    services.map((s) => [`${s.providerId}:${s.id}`, s])
  );
  const recentServices = [];
  const seen = new Set();
  for (const r of getRecent('services')) {
    const s = catalogByKey[r.id];
    if (s && !seen.has(r.id)) {
      recentServices.push(s);
      seen.add(r.id);
    }
    if (recentServices.length >= 10) break;
  }

  // Applications: resources grouped by an "app" tag (falling back to team/env).
  const appMap = {};
  for (const r of resources) {
    const key = r.tags?.app || r.tags?.team || r.tags?.env || 'ungrouped';
    const a = (appMap[key] ||= {
      name: key,
      count: 0,
      cost: 0,
      providers: new Set(),
      regions: new Set(),
    });
    a.count += 1;
    a.cost += r.monthlyCost || 0;
    a.providers.add(r.providerId);
    a.regions.add(r.region);
  }
  const applications = Object.values(appMap).sort((x, y) => y.cost - x.cost);

  const statusCounts = resources.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});
  const statusOrder = ['running', 'provisioning', 'stopped', 'error'];
  const topResources = [...resources]
    .sort((a, b) => b.monthlyCost - a.monthlyCost)
    .slice(0, 6);
  const changeDown = (costs.changePercent ?? 0) <= 0;
  const hasPrevious = (costs.previousMonthCost ?? 0) > 0;
  const trend = costs.dailyTrend || [];

  const openService = (s) => {
    recordVisit('services', {
      id: `${s.providerId}:${s.id}`,
      name: s.name,
      category: s.category,
      providerId: s.providerId,
    });
    navigate(s.category === 'Security' ? '/iam' : `/services/${s.providerId}/${s.id}`);
  };

  // Applications are a read-only rollup here: the Resources page they used to
  // link through to is hidden from navigation.

  return (
    <div className="stack">
      <PageHeader
        title="Console Home"
        subtitle="Unified view across your connected cloud accounts."
        actions={
          <>
            <Link className="btn btn--ghost" to="/services">
              View all services
            </Link>
            <button className="btn btn--primary" onClick={reload}>
              <Icon name={ICONS.refresh} /> Refresh
            </button>
          </>
        }
      />

      <div className="grid grid--kpi6">
        <StatCard className="stat-card--tight" label="Monthly spend" value={money(o.monthlyCost, o.currency)} trend={costs.changePercent || null} />
        <StatCard className="stat-card--tight" label="Forecast" value={money(o.forecastCost, o.currency)} hint="month end" />
        <StatCard className="stat-card--tight" label="Resources" value={number(o.totalResources)} hint={`${o.runningCount} running`} />
        <StatCard className="stat-card--tight" label="Running" value={number(o.runningCount)} hint={`of ${o.totalResources}`} />
        <StatCard className="stat-card--tight" label="Providers" value={number(o.totalProviders)} hint="connected" />
        <StatCard className="stat-card--tight" label="Regions" value={number(o.totalRegions)} hint="all clouds" />
      </div>

      <div className="dash">
        {/* Recently visited services */}
        <Widget
          className="span-6"
          title="Recently visited"
          info
          action={<Link className="link" to="/services">View all services</Link>}
          flush
        >
          {recentServices.length === 0 ? (
            <EmptyState
              title="No services visited yet"
              detail="Services you open from the catalog show up here."
            />
          ) : (
            <ul className="recent-list">
              {recentServices.map((s) => (
                <li key={`${s.providerId}:${s.id}`}>
                  <button className="recent-item" onClick={() => openService(s)}>
                    <span
                      className="recent-item__icon"
                      style={{
                        background: `${CATEGORY_COLORS[s.category] || '#64748b'}22`,
                        color: CATEGORY_COLORS[s.category] || '#64748b',
                      }}
                    >
                      {s.category.slice(0, 1)}
                    </span>
                    <span className="recent-item__body">
                      <div className="recent-item__name">{s.name}</div>
                      <div className="recent-item__sub">{s.category}</div>
                    </span>
                    <span className="recent-item__kind">{s.providerShort}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Widget>

        {/* Applications */}
        <Widget
          className="span-6"
          title={`Applications (${applications.length})`}
          info
          flush
        >
          {applications.length === 0 ? (
            <div className="apps-empty">
              <div className="apps-empty__title">No applications</div>
              <div className="apps-empty__sub">
                Tag resources with an <span className="mono">app</span> tag to group them here.
              </div>
            </div>
          ) : (
            <ul className="apps-list">
              {applications.map((a) => (
                <li key={a.name}>
                  <div className="app-row app-row--static">
                    <ProviderAvatar
                      provider={providersById[[...a.providers][0]]}
                      size={30}
                    />
                    <span>
                      <div className="app-row__name">{a.name}</div>
                      <div className="app-row__sub">
                        {a.count} resource{a.count === 1 ? '' : 's'} ·{' '}
                        {[...a.providers].length} provider
                        {[...a.providers].length === 1 ? '' : 's'} ·{' '}
                        {[...a.regions].length} region
                        {[...a.regions].length === 1 ? '' : 's'}
                      </div>
                    </span>
                    <span className="app-row__cost">
                      <div className="app-row__cost-val">{currency(a.cost, o.currency)}</div>
                      <div className="app-row__cost-sub">/ month</div>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Widget>

        {/* Cost and usage */}
        <Widget
          className="span-8"
          title="Cost and usage"
          info
          action={<Link className="link" to="/costs">Details</Link>}
        >
          <div className="costwidget">
            <div className="costwidget__figures">
              <div>
                <div className="costfig__label">Current month costs</div>
                <div className="costfig__value costfig__value--accent">
                  {money(costs.monthlyCost, costs.currency)}
                </div>
                {hasPrevious ? (
                  <div className={`costfig__delta costfig__delta--${changeDown ? 'down' : 'up'}`}>
                    {changeDown ? '↓' : '↑'} {Math.abs(costs.changePercent ?? 0).toFixed(1)}% vs last month
                  </div>
                ) : (
                  <div className="costfig__delta">run-rate of current inventory</div>
                )}
              </div>
              <div>
                <div className="costfig__label">Forecasted month end</div>
                <div className="costfig__value">{money(costs.forecastCost, costs.currency)}</div>
                {hasPrevious ? (
                  <div className="costfig__delta">
                    prev. {currency(costs.previousMonthCost, costs.currency)}
                  </div>
                ) : null}
              </div>
            </div>
            {trend.length > 0 ? (
              <div className="costwidget__chart">
                <div className="costwidget__axis">
                  <span>Cost ($) · daily</span>
                  <span>{currency(Math.max(...trend), costs.currency)}</span>
                </div>
                <MiniBars data={trend} />
                <div className="costwidget__axis">
                  <span>~30 days ago</span>
                  <span>today</span>
                </div>
              </div>
            ) : (
              <EmptyState
                title="No daily spend history"
                detail="Daily cost needs the provider's usage API; the figures above are the run-rate of the current inventory."
              />
            )}
          </div>
        </Widget>

        {/* Provider health */}
        <Widget
          className="span-4"
          title="Cloud accounts"
          info
          action={<Link className="link" to="/providers">View all</Link>}
          flush
        >
          {o.costByProvider.length === 0 ? (
            <EmptyState
              title="No cloud accounts"
              detail="Connect a provider integration to see it here."
            />
          ) : (
            <ul className="phealth">
              {o.costByProvider.map((p) => {
                const prov = providersById[p.providerId];
                return (
                  <li key={p.providerId} className="phealth__row">
                    <ProviderAvatar
                      provider={prov || { shortName: p.name, accentColor: p.accentColor }}
                      size={34}
                    />
                    <div>
                      <div className="phealth__name">{p.name}</div>
                      <div className="phealth__sub">{prov?.defaultRegion || '—'}</div>
                    </div>
                    <div className="phealth__cost">
                      <div className="phealth__cost-val">{money(p.monthlyCost, o.currency)}</div>
                      <StatusBadge status={prov?.status || 'operational'} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Widget>

        {/* Resources by category */}
        <Widget className="span-4" title="Resources by category" info>
          {o.resourcesByCategory.length === 0 ? (
            <EmptyState
              title="No resources"
              detail="Nothing is provisioned in the connected accounts."
            />
          ) : (
            <DonutChart
              centerValue={number(o.totalResources)}
              centerLabel="resources"
              segments={o.resourcesByCategory.map((c) => ({
                label: c.category,
                value: c.count,
                color: CATEGORY_COLORS[c.category] || '#64748b',
              }))}
            />
          )}
        </Widget>

        {/* Spend by provider */}
        <Widget className="span-4" title="Spend by provider" info>
          {o.monthlyCost > 0 ? (
            <BarList
              items={o.costByProvider.map((p) => ({
                label: p.name,
                value: p.monthlyCost,
                color: p.accentColor,
              }))}
              formatValue={(v) => currency(v, o.currency)}
            />
          ) : (
            <EmptyState title="No spend recorded" detail="No billable resources yet." />
          )}
        </Widget>

        {/* Resource status */}
        <Widget className="span-4" title="Resource status" info>
          <div className="statuses">
            {statusOrder.map((s) => {
              const meta = STATUS_META[s];
              return (
                <div key={s} className="status-cell">
                  <span className="status-cell__dot" style={{ background: meta.color }} />
                  <div>
                    <div className="status-cell__count">{number(statusCounts[s] || 0)}</div>
                    <div className="status-cell__label">{meta.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Widget>

        {/* Top resources by spend */}
        <Widget
          className="span-8"
          title="Top resources by spend"
          flush
        >
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Resource</th>
                  <th>Region</th>
                  <th>Status</th>
                  <th className="num">Monthly cost</th>
                </tr>
              </thead>
              <tbody>
                {topResources.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="table__empty">
                      No resources to display
                    </td>
                  </tr>
                ) : null}
                {topResources.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <div className="cell-provider">
                        <ProviderAvatar provider={providersById[r.providerId]} size={26} />
                        <div>
                          <div className="cell-primary">{r.name}</div>
                          <div className="cell-sub">
                            {r.category} · <span className="cell-type">{r.type}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="cell-type">{r.region}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="num">{currency(r.monthlyCost, o.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Widget>

        {/* Recent activity */}
        <Widget
          className="span-4"
          title="Recent activity"
        >
          {o.recentActivity.length === 0 ? (
            <EmptyState
              title="No activity"
              detail="Provisioning, scans, and alerts appear here as they happen."
            />
          ) : (
            <ul className="activity-list">
              {o.recentActivity.map((a) => (
                <li key={a.id} className="activity-list__item">
                  <StatusBadge status={a.severity} />
                  <span className="activity-list__msg">{a.message}</span>
                  <span className="activity-list__time">{relativeTime(a.timestamp)}</span>
                </li>
              ))}
            </ul>
          )}
        </Widget>
      </div>
    </div>
  );
}
